import React, { useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTooltip } from "react-common-components";
import Accordion, { CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER } from "src/components/Accordion";
import CheckPermission from "src/components/CheckPermission";
import Loader from "src/components/Loader";
import SortableHandle from "src/components/Sortable/Handle";
import Title from "src/components/Title";
import WrapText from "src/components/WrapText";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { IMetbe001001, IStrategyGrouper } from "../interface";
import { ContainerAccordion, StrategyQuadrants } from "../styles";
import Perspectives from "./Perspectives";
import SWOTItems from "./SWOTItems";

const StrategyGrouper: React.FC<IStrategyGrouper> = ({
	strategyData,
	refModalStrategy,
	refModalitem,
	permissions
}) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { administrator, superUser } = useAuth();

	const [expandedAccordion, setExpandedAccordion] = useState(false);

	const [ data, setData ] = useState<IMetbe001001 | null>(null);
	const [loading, setIsLoading] = useState(true);

  
	const loadData = async () => {
		try{
			setIsLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "Strategies/SelectStrategyContent",
				urlParameters: {
					id: strategyData.mee001Id
				}
			});
			setData(response.data);
			setIsLoading(false);	
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setIsLoading(false);
		}
	};

	const handleAccordion = (expanded:boolean) => {
		setExpandedAccordion(expanded);
		if(!expanded)
			return;
		loadData();
	};

	return (
		<>
			<ContainerAccordion>
				<div>
					<Accordion 
						expanded={expandedAccordion}
						onChange={(e, expanded) => {e.stopPropagation();  handleAccordion(expanded); }}
						header={
							<>
								<RCCGrid 
									container
									item
									justifyContent="space-between"
									alignItems="center"
									xs={12}
								>
									<RCCGrid
										item
										container
										xs={8}
										md={10}
									>
										<RCCGrid
											item
											styleInline="display: flex"
										>
											<WrapText>
												{strategyData.mee001Titulo}
											</WrapText>
										</RCCGrid>
									</RCCGrid>
									<RCCGrid
										container 
										item
										justifyContent="flex-end"
										xs={4}
										md={2}
										className={CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER}
									>
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
											{(permissions >= 2 || administrator || superUser) && (
												<RCCTooltip
													text={`${translate("Editar Estratégia")}`}
													color="grey"
												>
													<RCCButton
														icon={{name: "pencil", size: 12}}
														outlined
														color="grey"
														size="xs"
														layout="isRounded"
														onClick={(e) => {e.stopPropagation(); refModalStrategy.current?.actionButtonChange(strategyData); }}
													/>
												</RCCTooltip> 
											)}
										</CheckPermission>
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
											{(permissions >= 2 || administrator || superUser) && (
												<RCCTooltip
													text={`${translate("Excluir Estratégia")}`}
													color="red"
												>
													<RCCButton
														icon={{name: "trash", size: 12}}
														outlined
														color="red"
														size="xs"
														layout="isRounded"
														onClick={(e) => {e.stopPropagation(); refModalStrategy.current?.actionButtonDelete(strategyData);}}
													/>
												</RCCTooltip>	
											)}
										</CheckPermission>
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
											{(permissions >= 2 || administrator || superUser) && (
												<SortableHandle>
													<RCCTooltip 
														color="grey"
														text={`${translate("Mover Estratégia")}`}
													>
														<RCCButton
															color="grey"
															outlined
															size="xs"
															icon={{
																name: "arrows"
															}}
															layout="isRounded"
															onClick={(e) => e.stopPropagation()}
														/>
													</RCCTooltip>	
												</SortableHandle>
											)}
										</CheckPermission>
									</RCCGrid>
								</RCCGrid>
							</>}
					>
						{loading ? (
							<Loader />
						) : (
							<>
								{data && (
									<>
										<RCCGrid 
											container
											item
											xs={12}
											spacing={2}
											style={{ paddingTop: "8px" }}
										>
											<StrategyQuadrants item xs={12} md={6}> 
												<RCCGrid
													container
													item
													justifyContent="space-between"
													style={{ marginBottom: "1rem", height: "max-content" }}
												>
													<Title
														size="md"
														color="purple"
													>
														{translate("Perspectivas")}
													</Title>
													<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
														{(permissions >= 2 || administrator || superUser) && (
															<RCCButton
																color="purple"
																icon={{
																	name: "plus"
																}}
																layout="default"
																size="xs"
																onClick={(e) => {e.stopPropagation();  refModalitem.current?.actionButtonAdd(true, strategyData, loadData); }}
															>
																{translate("Adicionar Objetivo")}
															</RCCButton>
														)}
													</CheckPermission>												
												</RCCGrid>

												{data && data.metba002.length > 0 ? data.metba002.map((e) => (
													<Perspectives 
														key={v4()} 
														metba002={e} 
														metbe002={data.metbe002.filter((ef) => ef.mee002Mea002Id === e.mea002Id)}		
														refModalitem={refModalitem}	
														loadData={loadData}		
														permissions={permissions}								
													/>
												)) : (
													<RCCAlert
														color="purple"
														title={translate("Nenhum objetivo cadastrado")}
														description={translate("Adicione um objetivo para visualizá-lo")}
													/>
												)}	
											</StrategyQuadrants>
											<StrategyQuadrants item xs={12} md={6}> 
												<RCCGrid
													container
													item
													justifyContent="space-between"
													style={{ marginBottom: "1rem", height: "max-content" }}
												>
													<Title
														size="md"
														color="purple"
													>
														{translate("Itens da SWOT")}
													</Title>
													<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
														{(permissions >= 2 || administrator || superUser) && (
															<RCCButton
																color="purple"
																icon={{
																	name: "plus"
																}}
																layout="default"
																size="xs"
																onClick={(e) => {e.stopPropagation();  refModalitem.current?.actionButtonAdd(false, strategyData, loadData); }}
															>
																{translate("Adicionar Item da SWOT")}
															</RCCButton>
														)}
													</CheckPermission>
												</RCCGrid>
											
												{data && data.metbs002.length > 0 ? (
													<SWOTItems 
													 	metbs002={data.metbs002}	
														refModalitem={refModalitem}	
														loadData={loadData}		
														mee001Id={data.mee001Id}	
														permissions={permissions}								
													/>
												) : (
													<RCCAlert
														color="purple"
														title={translate("Nenhum item da SWOT cadastrado")}
														description={translate("Adicione um item para visualizá-lo")}
													/>
												)}		
											</StrategyQuadrants>
										</RCCGrid>
									</>
								)}
							</>
						)}	
					</Accordion>
				</div>
			</ContainerAccordion>
		</>
	);
};

export default StrategyGrouper;
